import { Injectable } from '@angular/core';
import { Observable, combineLatest } from 'rxjs';
import { distinctUntilChanged, map, take } from 'rxjs/operators';
import { SubMenuService } from './submenu.service';
import { CatchHeatService } from './catch-heat.service';
import { ComboService } from '../components/shared/components/combo/combo.service';
import { COMBO_LAYERS, ComboLayer } from '../components/shared/components/combo/combo.models';

/** Marker overlays (the old "Layers" sheet). Ids are what `SubMenuService.activeMapLayers$` already carries. */
export const MAP_PIN_LAYERS = ['buoy', 'local-tides', 'wrecks'] as const;
export type MapPinLayer = (typeof MAP_PIN_LAYERS)[number];

/** Catch heatmap (fleet-gated). Same key Flutter's rail uses. */
export const CATCH_HEAT_KEY = 'catch' as const;

/** One live entry across all families: a combo field (`sst`, `chl`, …), a pin layer or catch heat. */
export type MapLayerKey = ComboLayer | MapPinLayer | typeof CATCH_HEAT_KEY;

const isPin = (key: string): key is MapPinLayer => (MAP_PIN_LAYERS as readonly string[]).includes(key);

/**
 * The map's single layer picker model — port of Flutter's `MapLayerRail` selection rules
 * (`home_screen_layers_control.dart`, `_selectRailLayer` / `_closeLayerRail`).
 *
 * **Strictly one entry at a time**, marker layers included: picking Buoys takes the SST field down
 * and picking SST takes Buoys down. Nothing stacks, so the map only shows what the ring is on.
 * Selection applies immediately — there is no staging/Apply step.
 */
@Injectable({ providedIn: 'root' })
export class MapLayerSelectionService {
  /** Key of the live entry, or null when the map shows no layer. Combo wins if both are ever on. */
  readonly selected$: Observable<MapLayerKey | null>;

  constructor(
    private submenu: SubMenuService,
    private combo: ComboService,
    private heat: CatchHeatService,
  ) {
    this.selected$ = combineLatest([
      this.combo.state$.pipe(map((s) => (s.config.enabled ? s.config.layer : null))),
      this.heat.enabled$,
      this.submenu.activeMapLayers$.pipe(map((ids) => (ids ?? []).find(isPin) ?? null)),
    ]).pipe(
      map(([field, heat, pin]): MapLayerKey | null => field ?? (heat ? CATCH_HEAT_KEY : pin)),
      distinctUntilChanged(),
    );
  }

  /** Make `key` the one live layer and take everything else down. */
  select(key: MapLayerKey): void {
    this.clearLegacyOverlays();
    if (key === CATCH_HEAT_KEY) {
      if (this.combo.state.config.enabled) this.combo.disable();
      this.submenu.setActiveMapLayers([]);
      this.heat.enable();
      return;
    }
    // Heat down for every other pick; it owns map layers that must not outlive its selection.
    this.heat.disable();
    if (isPin(key)) {
      // Field down first: the combo engine owns the on-map card and must not outlive its pick.
      if (this.combo.state.config.enabled) this.combo.disable();
      this.submenu.setActiveMapLayers([key]);
      return;
    }
    this.submenu.setActiveMapLayers([]);
    if (this.combo.state.config.enabled) {
      // A plain field swap keeps the loaded frames and warmed manifests (Flutter's `selectLayer`).
      this.combo.selectLayer(key);
    } else {
      // Every field is reachable from the picker, so nothing is "unavailable" in the switcher.
      this.combo.enable(key, [...COMBO_LAYERS]);
    }
  }

  /** Take every layer down. */
  clear(): void {
    this.clearLegacyOverlays();
    this.heat.disable();
    if (this.combo.state.config.enabled) this.combo.disable();
    this.submenu.setActiveMapLayers([]);
  }

  /** Legacy ocean/weather overlays are mutually exclusive with the picker's layers. */
  private clearLegacyOverlays(): void {
    this.submenu.activeMapOverlays$.pipe(take(1)).subscribe((overlays) => {
      (overlays ?? []).forEach((o) => this.submenu.removeMapOverlay(o.type));
    });
  }
}
